/**
 * VENOA FAQ: exclusive accordion. At most one question is open — opening
 * another closes the current one, and clicking the open question closes it
 * again, so "all closed" is a valid state (and the state on page load).
 * State is exposed via aria-expanded / aria-controls / aria-hidden.
 * Scoped to this element only: sibling FAQ sections never affect each other.
 */
class VenoaFaq extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
    this.triggers = this.items.map((item) => item.querySelector('[data-faq-trigger]'));

    this.triggers.forEach((trigger, index) => {
      if (!trigger) return;
      trigger.addEventListener('click', () => this.toggle(index), { signal });
      trigger.addEventListener('keydown', (event) => this.onKeydown(event, index), { signal });
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  toggle(index) {
    const item = this.items[index];
    if (!item) return;

    if (item.classList.contains('is-open')) {
      this.close(index);
      return;
    }

    this.items.forEach((_, i) => {
      if (i !== index) this.close(i);
    });
    this.open(index);
  }

  open(index) {
    const item = this.items[index];
    const trigger = this.triggers[index];
    if (!item || !trigger) return;
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    item.querySelector('[data-faq-panel]')?.setAttribute('aria-hidden', 'false');
  }

  close(index) {
    const item = this.items[index];
    const trigger = this.triggers[index];
    if (!item || !trigger) return;
    item.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    item.querySelector('[data-faq-panel]')?.setAttribute('aria-hidden', 'true');
  }

  onKeydown(event, index) {
    const keys = { ArrowDown: 1, ArrowUp: -1, Home: 'home', End: 'end' };
    const move = keys[event.key];
    if (move === undefined) return;
    event.preventDefault();
    const count = this.triggers.length;
    let next = index;
    if (move === 'home') next = 0;
    else if (move === 'end') next = count - 1;
    else next = (index + move + count) % count;
    this.triggers[next]?.focus();
  }
}

if (!customElements.get('venoa-faq')) {
  customElements.define('venoa-faq', VenoaFaq);
}
